import { useSelector } from 'react-redux';
import {
    Badge,
    Box,
    HStack,
    SimpleGrid,
    VStack,
    useRadio,
    useRadioGroup,
} from '@chakra-ui/react';
import { Button, Heading, Image, Text } from '@/components/v4';
import { SBCAAS_MODERN_LABEL } from '@/constants/sbcaas';
import CardDetails from 'pages/dashboards/v5/components/CardDetails';
import { SBC, SRT } from 'pages/dashboards/v5/constants';

/**
 * Which service a new trunk is built against.
 *
 * Shown only when a company runs both SIP trunk services — with one service
 * there is nothing to choose, so the caller skips straight to the trunk itself.
 * The modern service leads and starts selected, as it is the recommended choice
 * for new trunks.
 */
const RECOMMENDED_SERVICE = SRT;
const SERVICE_ORDER = [SRT, SBC];

const ServiceOption = ({ serviceType, darkMode, ...radioProps }) => {
    const { serviceName, icon, addDescription } = CardDetails(serviceType);
    const { getInputProps, getCheckboxProps, state } = useRadio(radioProps);
    const isChecked = state.isChecked;

    return (
        <Box as="label" height="100%">
            <input {...getInputProps()} />
            <Box
                {...getCheckboxProps()}
                cursor="pointer"
                borderRadius={15}
                padding="24px"
                height="100%"
                bg={
                    isChecked
                        ? darkMode
                            ? 'whiteAlpha.100'
                            : 'blue.50'
                        : darkMode
                          ? 'dark.tcap'
                          : 'white'
                }
                border={isChecked ? '2px solid' : '1px solid'}
                borderColor={
                    isChecked
                        ? 'brand.500'
                        : darkMode
                          ? 'dark.tcap-borderGray'
                          : 'gray.200'
                }
                _hover={{ borderColor: 'brand.500' }}
                _focusVisible={{ boxShadow: 'outline' }}>
                <VStack align="flex-start" spacing={3} height="100%">
                    <HStack width="100%" justify="space-between">
                        <Image src={icon} sx={{ maxWidth: '50px' }} />
                        <Box
                            aria-hidden
                            boxSize="20px"
                            borderRadius="full"
                            border="2px solid"
                            borderColor={isChecked ? 'brand.500' : 'gray.400'}
                            display="flex"
                            alignItems="center"
                            justifyContent="center">
                            {isChecked && (
                                <Box
                                    boxSize="10px"
                                    borderRadius="full"
                                    bg="brand.500"
                                />
                            )}
                        </Box>
                    </HStack>
                    <HStack spacing={2}>
                        <Heading as="h4" size="sm" marginBottom={0}>
                            {serviceName}
                        </Heading>
                        {serviceType === RECOMMENDED_SERVICE && (
                            <Badge colorScheme="green" textTransform="none">
                                Recommended
                            </Badge>
                        )}
                    </HStack>
                    <Text fontSize="sm" marginBottom={0}>
                        {addDescription}
                    </Text>
                </VStack>
            </Box>
        </Box>
    );
};

const TrunkServiceChoice = ({ onSelect, onBack }) => {
    const { darkMode } = useSelector((state) => state.settings);
    const { getRootProps, getRadioProps, value } = useRadioGroup({
        name: 'trunkService',
        defaultValue: RECOMMENDED_SERVICE,
    });

    return (
        <>
            <Heading
                as="h3"
                fontSize="lg"
                fontWeight={600}
                mb={1}
                color={darkMode ? 'white' : '#262626'}>
                Which service is this trunk for?
            </Heading>
            <Text fontSize="sm" mb={4}>
                We recommend {SBCAAS_MODERN_LABEL} for new trunks.
            </Text>
            <SimpleGrid
                {...getRootProps()}
                columns={{ base: 1, md: 2 }}
                spacing={4}
                maxWidth={895}>
                {SERVICE_ORDER.map((serviceType) => (
                    <ServiceOption
                        key={serviceType}
                        serviceType={serviceType}
                        darkMode={darkMode}
                        {...getRadioProps({ value: serviceType })}
                    />
                ))}
            </SimpleGrid>

            <Box sx={{ marginTop: '25px', display: 'flex', gap: 2 }}>
                <Button
                    variant="outline"
                    sx={{ background: 'white', minWidth: '120px' }}
                    onClick={onBack}>
                    Back
                </Button>
                <Button
                    sx={{ minWidth: '120px' }}
                    isDisabled={!value}
                    onClick={() => onSelect(value)}>
                    Continue
                </Button>
            </Box>
        </>
    );
};

export default TrunkServiceChoice;
